import React, { useState, useEffect } from 'react';
import api from '../api';
import { formatCurrencyAmount, summarizeInvoiceTotals } from '../currency';
import {
  emptyAsDash,
  filterSavedInvoices,
  invoiceGoodsTotal,
  invoiceItemCount,
  invoiceReference,
  invoiceStemCount,
} from '../invoiceList';
import { getInvoiceValidation } from '../invoiceValidation';
import { INVOICE_SECTIONS } from '../invoiceNavigation';

function ResponsiveTable({ children, ...tableProps }) {
  return <div className="workspace-table responsive-table"><table {...tableProps}>{children}</table></div>;
}

const invoiceQueueKey = (invoice) => invoice?._queue_key
  || (invoice?.invoice_number ? `invoice:${invoice.invoice_number}` : '')
  || (invoice?.awb_number ? `awb:${invoice.awb_number}` : '')
  || `supplier:${invoice?.supplier_id || 'unknown'}:${invoice?.total_amount || 0}`;

const fetchAccessibleInvoices = async () => {
  const perPage = 200;
  let page = 1;
  const result = [];

  while (true) {
    const response = await api.get('/tenant/invoices', { params: { page, per_page: perPage } });
    const batch = Array.isArray(response.data) ? response.data : [];
    result.push(...batch);
    const total = Number(response.headers?.['x-total-count']);
    if (batch.length < perPage || (Number.isFinite(total) && result.length >= total)) break;
    page += 1;
  }

  return result;
};

function PurchaserDashboard({ activeTab: requestedTab, embeddedNavigation = false, onTabChange } = {}) {
  const [suppliers, setSuppliers] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [mode, setMode] = useState('fast');
  const [internalTab, setInternalTab] = useState('invoices');
  const activeTab = requestedTab ?? internalTab;
  const setActiveTab = onTabChange || setInternalTab;

  // Быстрый ввод
  const [fastRows, setFastRows] = useState([{
    supplier_id: '',
    invoice_number: '',
    total_amount: '',
    invoice_date: '',
    delivery_date: '',
    consignee: ''
  }]);

  // Подробный ввод
  const [detailedForm, setDetailedForm] = useState({
    supplier_id: '',
    invoice_number: '',
    invoice_date: '',
    delivery_date: '',
    consignee: '',
    total_amount: '',
    upload_type: 'import',
    awb_number: '',
    transport_weight: '',
    transport_cost: '',
    supplier_commission: '',
    packaging_cost: '',
    packaging_details: [],
    items: [{
      flower_type: '', variety: '', length: '', stems_per_bunch: '',
      box_dimension: '', box_number: '',
      bunches_per_box: '', total_stems: '', price_per_stem: '', total_line: ''
    }]
  });

  // Проверка инвойса
  const [checkSupplier, setCheckSupplier] = useState('');
  const [lastInvoices, setLastInvoices] = useState([]);

  // Пакетная загрузка
  const [pdfLoading, setPdfLoading] = useState(false);
  const [parsedInvoices, setParsedInvoices] = useState([]);
  const [savingId, setSavingId] = useState(null);

  // Модальное редактирование
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ ...detailedForm });

  // Параметры выгрузки
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [deliveryDates, setDeliveryDates] = useState([]);

  // Дата и грузополучатель для массового сохранения
  const [queueDeliveryDate, setQueueDeliveryDate] = useState('');
  const [queueConsignee, setQueueConsignee] = useState('');

  // Номер инвойса, открытого в подробной форме
  const [filledInvoiceKey, setFilledInvoiceKey] = useState(null);

  // Модальное окно RAW
  const [rawModalOpen, setRawModalOpen] = useState(false);
  const [rawText, setRawText] = useState('');

  // Просмотр сохранённых инвойсов
  const [savedFilters, setSavedFilters] = useState({ supplierId: '', deliveryDate: '', query: '' });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [savedInvoicesLoading, setSavedInvoicesLoading] = useState(true);
  const [savedInvoicesError, setSavedInvoicesError] = useState('');


  useEffect(() => {
    if (editModalOpen || rawModalOpen || selectedInvoice) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [editModalOpen, rawModalOpen, selectedInvoice]);

  useEffect(() => {
    api.get('/tenant/suppliers').then(res => {
      const sorted = [...res.data].sort((a, b) => a.name.localeCompare(b.name));
      setSuppliers(sorted);
    });
    api.get('/tenant/consignees').then(res => setConsignees(res.data));
    fetchAccessibleInvoices()
      .then(setInvoices)
      .catch(() => setSavedInvoicesError('Не удалось загрузить сохранённые инвойсы'))
      .finally(() => setSavedInvoicesLoading(false));
    api.get('/tenant/invoices/delivery-dates')
      .then(res => setDeliveryDates(res.data.dates || []))
      .catch(() => {});
  }, []);

  const refreshSavedInvoices = async () => {
    try {
      setSavedInvoicesError('');
      setInvoices(await fetchAccessibleInvoices());
    } catch (err) {
      setSavedInvoicesError('Не удалось обновить сохранённые инвойсы');
    }
  };

  useEffect(() => {
    if (checkSupplier) {
      api.get('/tenant/invoices/last', { params: { supplier_id: checkSupplier } })
        .then(res => setLastInvoices(res.data))
        .catch(() => setLastInvoices([]));
    } else {
      setLastInvoices([]);
    }
  }, [checkSupplier]);

  // --- Дообучение парсера ---
  const handleLearnParser = async (invoice) => {
    if (!window.confirm('Запустить дообучение парсера для этого инвойса?')) return;
    
    try {
      const resp = await api.post('/tenant/parser-learn', {
        raw_text: invoice._raw_text || '',
        supplier_name: invoice.supplier_name || '',
        invoice_data: invoice
      });
      alert(resp.data.msg + '\n\nОбновите страницу и загрузите PDF заново.');
      const queueKey = invoiceQueueKey(invoice);
      setParsedInvoices(prev => prev.filter(inv => invoiceQueueKey(inv) !== queueKey));
    } catch (err) {
      alert('Ошибка дообучения: ' + (err.response?.data?.detail || ''));
    }
  };

  // --- Быстрый ввод ---
  const handleFastChange = (index, field, value) => {
    const newRows = [...fastRows];
    newRows[index][field] = value;
    setFastRows(newRows);
  };

  const addFastRow = () => {
    if (fastRows.length >= 100) return;
    setFastRows([...fastRows, {
      supplier_id: '', invoice_number: '', total_amount: '',
      invoice_date: '', delivery_date: '', consignee: ''
    }]);
  };

  const removeFastRow = (index) => {
    if (fastRows.length === 1) return;
    const newRows = fastRows.filter((_, i) => i !== index);
    setFastRows(newRows);
  };

  const handleFastSubmit = async (e) => {
    e.preventDefault();
    for (const row of fastRows) {
      if (!row.supplier_id || !row.invoice_number || !row.total_amount) continue;
      const payload = {
        company_id: localStorage.getItem('x_company_id'),
        supplier_id: row.supplier_id,
        invoice_number: row.invoice_number,
        invoice_date: row.invoice_date || null,
        delivery_date: row.delivery_date || null,
        consignee: row.consignee,
        total_amount: parseFloat(row.total_amount),
        upload_type: 'import',
        items: [{
          product_name: 'Быстрый ввод',
          quantity: 1,
          unit: 'kg',
          price_per_unit: parseFloat(row.total_amount),
          total_line: parseFloat(row.total_amount)
        }]
      };
      try {
        await api.post('/tenant/invoices', payload);
      } catch (err) {
        alert(err.response?.data?.detail || 'Ошибка');
        return;
      }
    }
    alert('Все инвойсы добавлены');
    setFastRows([{ supplier_id: '', invoice_number: '', total_amount: '', invoice_date: '', delivery_date: '', consignee: '' }]);
    await refreshSavedInvoices();
  };

  // --- Подробный ввод ---
  const handleItemChange = (index, field, value) => {
    const newItems = [...detailedForm.items];
    newItems[index][field] = value;
    if (field === 'total_stems' || field === 'price_per_stem') {
      const stems = parseFloat(newItems[index].total_stems) || 0;
      const price = parseFloat(newItems[index].price_per_stem) || 0;
      newItems[index].total_line = (stems * price).toFixed(2);
    }
    setDetailedForm({ ...detailedForm, items: newItems });
  };

  const addDetailedItem = () => {
    setDetailedForm({
      ...detailedForm,
      items: [...detailedForm.items, {
        flower_type: '', variety: '', length: '', stems_per_bunch: '',
        box_dimension: '', box_number: '',
        bunches_per_box: '', total_stems: '', price_per_stem: '', total_line: ''
      }]
    });
  };

  const removeDetailedItem = (index) => {
    if (detailedForm.items.length === 1) return;
    const newItems = detailedForm.items.filter((_, i) => i !== index);
    setDetailedForm({ ...detailedForm, items: newItems });
  };

  const handleDetailedSubmit = async (e) => {
    e.preventDefault();
    if (!detailedForm.supplier_id) {
      alert('Поставщик не найден');
      return;
    }
    const payload = {
      company_id: localStorage.getItem('x_company_id'),
      supplier_id: detailedForm.supplier_id,
      invoice_number: detailedForm.invoice_number,
      invoice_date: detailedForm.invoice_date || null,
      delivery_date: detailedForm.delivery_date || null,
      consignee: detailedForm.consignee || '',
      total_amount: parseFloat(detailedForm.total_amount) || detailedForm.items.reduce((sum, item) => sum + parseFloat(item.total_line || 0), 0),
      upload_type: detailedForm.upload_type,
      awb_number: detailedForm.awb_number,
      transport_weight: parseFloat(detailedForm.transport_weight) || null,
      transport_cost: parseFloat(detailedForm.transport_cost) || null,
      supplier_commission: parseFloat(detailedForm.supplier_commission) || null,
      packaging_cost: parseFloat(detailedForm.packaging_cost) || null,
      packaging_details: detailedForm.packaging_details,
      items: detailedForm.items.map(item => ({
        flower_type: item.flower_type || '',
        variety: item.variety || '',
        length: item.length || '',
        stems_per_bunch: parseInt(item.stems_per_bunch) || 0,
        box_dimension: item.box_dimension || '',
        box_number: item.box_number || '',
        bunches_per_box: parseInt(item.bunches_per_box) || 0,
        total_stems: parseInt(item.total_stems) || 0,
        price_per_stem: parseFloat(item.price_per_stem) || 0,
        total_line: parseFloat(item.total_line) || 0,
        unit: 'stem'
      }))
    };
    try {
      await api.post('/tenant/invoices', payload);
      alert('Инвойс добавлен');
      if (filledInvoiceKey) {
        setParsedInvoices(prev => prev.filter(inv => invoiceQueueKey(inv) !== filledInvoiceKey));
        setFilledInvoiceKey(null);
      }
      setDetailedForm({
        supplier_id: '', invoice_number: '', invoice_date: '', delivery_date: '', consignee: '',
        total_amount: '', upload_type: 'import', awb_number: '', transport_weight: '', transport_cost: '',
        supplier_commission: '', packaging_cost: '', packaging_details: [],
        items: [{ flower_type: '', variety: '', length: '', stems_per_bunch: '', box_dimension: '', box_number: '', bunches_per_box: '', total_stems: '', price_per_stem: '', total_line: '' }]
      });
      await refreshSavedInvoices();
    } catch (err) {
      alert(err.response?.data?.detail || 'Ошибка');
    }
  };

  // --- Пакетная загрузка PDF ---
  const handleBatchUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setPdfLoading(true);
    const results = [];
    for (let file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const resp = await api.post('/tenant/upload-file', formData);
        const data = resp.data;
        data._queue_key = `${file.name}:${Date.now()}:${results.length}`;
        if (!data._raw_text) {
          data._raw_text = '';
        }
        if (data.supplier_id) {
          results.push(data);
        } else {
          alert(`Файл ${file.name}: поставщик не найден`);
        }
      } catch (err) {
        alert(`Файл ${file.name}: ${err.response?.data?.detail || 'Ошибка загрузки'}`);
      }
    }
    setParsedInvoices(prev => [...prev, ...results]);
    setPdfLoading(false);
  };

  // --- Заполнить форму ---
  const fillDetailedForm = (invoice) => {
    setDetailedForm({
      supplier_id: invoice.supplier_id || '',
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date || '',
      delivery_date: invoice.delivery_date || '',
      consignee: invoice.consignee || '',
      total_amount: invoice.total_amount?.toString() || '',
      upload_type: 'import',
      awb_number: invoice.awb_number || '',
      transport_weight: invoice.transport_weight?.toString() || '',
      transport_cost: invoice.transport_cost?.toString() || '',
      supplier_commission: invoice.supplier_commission?.toString() || '',
      packaging_cost: invoice.packaging_cost?.toString() || '',
      packaging_details: invoice.packaging_details || [],
      items: (invoice.items || []).map(item => ({
        flower_type: item.flower_type || '',
        variety: item.variety || '',
        length: item.length || '',
        stems_per_bunch: item.stems_per_bunch?.toString() || '',
        box_dimension: item.box_dimension || '',
        box_number: item.box_number || '',
        bunches_per_box: item.bunches_per_box?.toString() || '',
        total_stems: item.total_stems?.toString() || '',
        price_per_stem: item.price_per_stem?.toString() || '',
        total_line: item.total_line?.toString() || ''
      }))
    });
    setFilledInvoiceKey(invoiceQueueKey(invoice));
    setMode('detailed');
  };

  // --- Сразу сохранить инвойс ---
  const handleSaveOne = async (invoice) => {
    const validation = getInvoiceValidation(invoice);
    if (!validation.valid) {
      alert(`Инвойс нельзя сохранить:\n${validation.issues.join('\n')}`);
      return;
    }
    const queueKey = invoiceQueueKey(invoice);
    setSavingId(queueKey);
    const payload = {
      company_id: localStorage.getItem('x_company_id'),
      supplier_id: invoice.supplier_id,
      invoice_number: invoice.invoice_number,
      invoice_date: invoice.invoice_date || null,
      delivery_date: invoice.delivery_date || null,
      consignee: invoice.consignee || '',
      total_amount: invoice.total_amount,
      upload_type: 'import',
      awb_number: invoice.awb_number || '',
      transport_weight: invoice.transport_weight || null,
      transport_cost: invoice.transport_cost || null,
      supplier_commission: invoice.supplier_commission || null,
      packaging_cost: invoice.packaging_cost || null,
      packaging_details: invoice.packaging_details || [],
      items: (invoice.items || []).map(item => ({
        flower_type: item.flower_type || '',
        variety: item.variety || '',
        length: item.length || '',
        stems_per_bunch: parseInt(item.stems_per_bunch) || 0,
        box_dimension: item.box_dimension || '',
        box_number: item.box_number || '',
        bunches_per_box: parseInt(item.bunches_per_box) || 0,
        total_stems: parseInt(item.total_stems) || 0,
        price_per_stem: parseFloat(item.price_per_stem) || 0,
        total_line: parseFloat(item.total_line) || 0,
        unit: 'stem'
      }))
    };
    try {
      await api.post('/tenant/invoices', payload);
      alert(`Инвойс ${invoice.invoice_number} сохранён`);
      setParsedInvoices(prev => prev.filter(inv => invoiceQueueKey(inv) !== queueKey));
      await refreshSavedInvoices();
    } catch (err) {
      alert(err.response?.data?.detail || 'Ошибка');
    } finally {
      setSavingId(null);
    }
  };

  // --- Удаление из очереди ---
  const handleRemoveParsed = (invoice) => {
    const queueKey = invoiceQueueKey(invoice);
    setParsedInvoices(prev => prev.filter(inv => invoiceQueueKey(inv) !== queueKey));
  };

  // --- Показать RAW-текст ---
  const handleShowRaw = (invoice) => {
    const raw = invoice._raw_text || '';
    if (!raw) {
      setRawText(JSON.stringify(invoice, null, 2));
    } else {
      setRawText(raw);
    }
    setRawModalOpen(true);
  };

  // --- Модальное редактирование ---
  const openEditModal = (invoice) => {
    setEditForm({
      ...detailedForm,
      supplier_id: invoice.supplier_id || '',
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date || '',
      delivery_date: invoice.delivery_date || '',
      consignee: invoice.consignee || '',
      total_amount: invoice.total_amount?.toString() || '',
      upload_type: 'import',
      awb_number: invoice.awb_number || '',
      transport_weight: invoice.transport_weight?.toString() || '',
      transport_cost: invoice.transport_cost?.toString() || '',
      supplier_commission: invoice.supplier_commission?.toString() || '',
      packaging_cost: invoice.packaging_cost?.toString() || '',
      _queue_key: invoiceQueueKey(invoice),
      packaging_details: invoice.packaging_details || [],
      items: (invoice.items || []).map(item => ({
        flower_type: item.flower_type || '',
        variety: item.variety || '',
        length: item.length || '',
        stems_per_bunch: item.stems_per_bunch?.toString() || '',
        box_dimension: item.box_dimension || '',
        box_number: item.box_number || '',
        bunches_per_box: item.bunches_per_box?.toString() || '',
        total_stems: item.total_stems?.toString() || '',
        price_per_stem: item.price_per_stem?.toString() || '',
        total_line: item.total_line?.toString() || ''
      }))
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!editForm.supplier_id) return;
    const payload = {
      company_id: localStorage.getItem('x_company_id'),
      supplier_id: editForm.supplier_id,
      invoice_number: editForm.invoice_number,
      invoice_date: editForm.invoice_date || null,
      delivery_date: editForm.delivery_date || null,
      consignee: editForm.consignee || '',
      total_amount: parseFloat(editForm.total_amount) || editForm.items.reduce((sum, item) => sum + parseFloat(item.total_line || 0), 0),
      upload_type: 'import',
      awb_number: editForm.awb_number,
      transport_weight: parseFloat(editForm.transport_weight) || null,
      transport_cost: parseFloat(editForm.transport_cost) || null,
      supplier_commission: parseFloat(editForm.supplier_commission) || null,
      packaging_cost: parseFloat(editForm.packaging_cost) || null,
      packaging_details: editForm.packaging_details,
      items: editForm.items.map(item => ({
        flower_type: item.flower_type || '',
        variety: item.variety || '',
        length: item.length || '',
        stems_per_bunch: parseInt(item.stems_per_bunch) || 0,
        box_dimension: item.box_dimension || '',
        box_number: item.box_number || '',
        bunches_per_box: parseInt(item.bunches_per_box) || 0,
        total_stems: parseInt(item.total_stems) || 0,
        price_per_stem: parseFloat(item.price_per_stem) || 0,
        total_line: parseFloat(item.total_line) || 0,
        unit: 'stem'
      }))
    };
    try {
      await api.post('/tenant/invoices', payload);
      alert(`Инвойс ${editForm.invoice_number} сохранён`);
      setParsedInvoices(prev => prev.filter(inv => invoiceQueueKey(inv) !== editForm._queue_key));
      const resp = await api.get('/tenant/invoices');
      setInvoices(resp.data);
      setEditModalOpen(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Ошибка');
    }
  };

  // --- Массовое сохранение ---
  const handleSaveAll = async () => {
    const invalidInvoices = parsedInvoices.filter(inv => !getInvoiceValidation(inv).valid);
    if (invalidInvoices.length > 0) {
      alert(`Нельзя сохранить пакет: ${invalidInvoices.length} инвойс(ов) требуют проверки`);
      return;
    }
    for (const inv of parsedInvoices) {
      if (!inv.supplier_id) continue;
      const payload = {
        company_id: localStorage.getItem('x_company_id'),
        supplier_id: inv.supplier_id,
        invoice_number: inv.invoice_number,
        invoice_date: inv.invoice_date || null,
        delivery_date: queueDeliveryDate || inv.delivery_date || null,
        consignee: queueConsignee || inv.consignee || '',
        total_amount: inv.total_amount,
        upload_type: 'import',
        awb_number: inv.awb_number || '',
        transport_weight: inv.transport_weight || null,
        transport_cost: inv.transport_cost || null,
        supplier_commission: inv.supplier_commission || null,
        packaging_cost: inv.packaging_cost || null,
        packaging_details: inv.packaging_details || [],
        items: (inv.items || []).map(item => ({
          flower_type: item.flower_type || '',
          variety: item.variety || '',
          length: item.length || '',
          stems_per_bunch: parseInt(item.stems_per_bunch) || 0,
          box_dimension: item.box_dimension || '',
          box_number: item.box_number || '',
          bunches_per_box: parseInt(item.bunches_per_box) || 0,
          total_stems: parseInt(item.total_stems) || 0,
          price_per_stem: parseFloat(item.price_per_stem) || 0,
          total_line: parseFloat(item.total_line) || 0,
          unit: 'stem'
        }))
      };
      try {
        await api.post('/tenant/invoices', payload);
      } catch (err) {
        alert(`Ошибка при сохранении ${inv.invoice_number}: ${err.response?.data?.detail || ''}`);
        return;
      }
    }
    alert('Все инвойсы сохранены');
    setParsedInvoices([]);
    await refreshSavedInvoices();
  };

  const totalQueueAmount = summarizeInvoiceTotals(parsedInvoices);
  const totalQueueStems = parsedInvoices.reduce((sum, inv) => sum + (inv.items || []).reduce((s, it) => s + (it.total_stems || 0), 0), 0);
  const filteredSavedInvoices = filterSavedInvoices(invoices, savedFilters)
    .slice()
    .sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
  const savedInvoiceTotals = summarizeInvoiceTotals(filteredSavedInvoices);

  // --- Рендер вкладок ---
  const renderTabContent = () => {
    switch (activeTab) {
      case 'invoices':
        return (
          <div>
            <h3>Внесение инвойсов</h3>
            <div style={{ margin: '15px 0' }}>
              <button onClick={() => setMode('fast')} style={{ backgroundColor: mode === 'fast' ? '#1976d2' : '#ccc', color: mode === 'fast' ? 'white' : 'black', marginRight: 10 }}>Быстрый ввод</button>
              <button onClick={() => setMode('detailed')} style={{ backgroundColor: mode === 'detailed' ? '#1976d2' : '#ccc', color: mode === 'detailed' ? 'white' : 'black' }}>Подробный ввод</button>
            </div>
            {mode === 'fast' ? (
              <div>
                <h4>Быстрый ввод инвойсов</h4>
                <form onSubmit={handleFastSubmit}>
                  <ResponsiveTable border="1" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Дата поставки</th><th>Поставщик</th><th>Номер инвойса</th><th>Дата инвойса</th><th>Сумма</th><th>Грузополучатель</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {fastRows.map((row, idx) => (
                        <tr key={idx}>
                          <td><input type="date" value={row.delivery_date} onChange={e => handleFastChange(idx, 'delivery_date', e.target.value)} /></td>
                          <td>
                            <select value={row.supplier_id} onChange={e => handleFastChange(idx, 'supplier_id', e.target.value)} required>
                              <option value="">Выберите</option>
                              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </td>
                          <td><input type="text" value={row.invoice_number} onChange={e => handleFastChange(idx, 'invoice_number', e.target.value)} required /></td>
                          <td><input type="date" value={row.invoice_date} onChange={e => handleFastChange(idx, 'invoice_date', e.target.value)} /></td>
                          <td><input type="number" value={row.total_amount} onChange={e => handleFastChange(idx, 'total_amount', e.target.value)} required /></td>
                          <td>
                            <select value={row.consignee} onChange={e => handleFastChange(idx, 'consignee', e.target.value)}>
                              <option value="">Выберите</option>
                              {consignees.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                            </select>
                          </td>
                          <td><button type="button" onClick={() => removeFastRow(idx)} disabled={fastRows.length === 1}>✕</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </ResponsiveTable>
                  <button type="button" onClick={addFastRow} disabled={fastRows.length >= 100}>+ Добавить строку</button>
                  <button type="submit">Сохранить все</button>
                </form>
              </div>
            ) : (
              <div>
                <h4>Подробный ввод инвойса</h4>
                <form onSubmit={handleDetailedSubmit}>
                  <div className="invoice-fields-grid" style={{ gap: '10px' }}>
                    <label>Дата поставки: <input type="date" value={detailedForm.delivery_date} onChange={e => setDetailedForm({...detailedForm, delivery_date: e.target.value})} /></label>
                    <label>Грузополучатель:
                      <select value={detailedForm.consignee} onChange={e => setDetailedForm({...detailedForm, consignee: e.target.value})}>
                        <option value="">Выберите</option>
                        {consignees.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </select>
                    </label>
                    <label>Поставщик:
                      <select value={detailedForm.supplier_id} onChange={e => setDetailedForm({...detailedForm, supplier_id: e.target.value})} required>
                        <option value="">Выберите</option>
                        {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </label>
                    <label>Номер инвойса: <input type="text" value={detailedForm.invoice_number} onChange={e => setDetailedForm({...detailedForm, invoice_number: e.target.value})} required /></label>
                    <label>Дата инвойса: <input type="date" value={detailedForm.invoice_date} onChange={e => setDetailedForm({...detailedForm, invoice_date: e.target.value})} /></label>
                    <label>AWB номер: <input type="text" value={detailedForm.awb_number} onChange={e => setDetailedForm({...detailedForm, awb_number: e.target.value})} /></label>
                    <label>Вес поставки (кг): <input type="number" value={detailedForm.transport_weight} onChange={e => setDetailedForm({...detailedForm, transport_weight: e.target.value})} /></label>
                    <label>Стоимость транспорта: <input type="number" value={detailedForm.transport_cost} onChange={e => setDetailedForm({...detailedForm, transport_cost: e.target.value})} /></label>
                    <label>Комиссия поставщика: <input type="number" value={detailedForm.supplier_commission} onChange={e => setDetailedForm({...detailedForm, supplier_commission: e.target.value})} /></label>
                    <label>Стоимость упаковки: <input type="number" value={detailedForm.packaging_cost} onChange={e => setDetailedForm({...detailedForm, packaging_cost: e.target.value})} /></label>
                  </div>

                  {detailedForm.packaging_details && detailedForm.packaging_details.length > 0 && (
                    <div style={{ marginTop: '10px', border: '1px solid #ccc', padding: '8px' }}>
                      <h4>Упаковка поставки</h4>
                      <ResponsiveTable border="1" style={{ width: '100%' }}>
                        <thead><tr><th>Количество коробок</th><th>Код коробки</th><th>Тип коробки</th><th>Стоимость за коробки</th></tr></thead>
                        <tbody>
                          {detailedForm.packaging_details.map((pkg, idx) => (
                            <tr key={idx}><td>{pkg.qua}</td><td>{pkg.code}</td><td>{pkg.description}</td><td>{pkg.amount}</td></tr>
                          ))}
                        </tbody>
                      </ResponsiveTable>
                    </div>
                  )}

                  <h4>Позиции</h4>
                  <ResponsiveTable border="1" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Вид цветка</th><th>Сорт</th><th>Высота</th><th>Разбанчевка</th><th>Тип коробки</th><th>Номер коробки</th><th>Банчей в коробке</th><th>Итого стеблей</th><th>Цена за штуку</th><th>Цена общая</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailedForm.items.map((item, idx) => (
                        <tr key={idx}>
                          <td><input type="text" value={item.flower_type} onChange={e => handleItemChange(idx, 'flower_type', e.target.value)} style={{ width: '140px' }} /></td>
                          <td><input type="text" value={item.variety} onChange={e => handleItemChange(idx, 'variety', e.target.value)} style={{ width: '180px' }} /></td>
                          <td><input type="text" value={item.length} onChange={e => handleItemChange(idx, 'length', e.target.value)} /></td>
                          <td><input type="number" value={item.stems_per_bunch} onChange={e => handleItemChange(idx, 'stems_per_bunch', e.target.value)} /></td>
                          <td><input type="text" value={item.box_dimension} onChange={e => handleItemChange(idx, 'box_dimension', e.target.value)} /></td>
                          <td><input type="text" value={item.box_number} onChange={e => handleItemChange(idx, 'box_number', e.target.value)} /></td>
                          <td><input type="number" value={item.bunches_per_box} onChange={e => handleItemChange(idx, 'bunches_per_box', e.target.value)} /></td>
                          <td><input type="number" value={item.total_stems} onChange={e => handleItemChange(idx, 'total_stems', e.target.value)} /></td>
                          <td><input type="number" value={item.price_per_stem} onChange={e => handleItemChange(idx, 'price_per_stem', e.target.value)} /></td>
                          <td><input type="number" value={item.total_line} readOnly /></td>
                          <td><button type="button" onClick={() => removeDetailedItem(idx)} disabled={detailedForm.items.length === 1}>✕</button></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="7">Итого:</td>
                        <td>{detailedForm.items.reduce((sum, item) => sum + (parseInt(item.total_stems) || 0), 0)}</td>
                        <td></td>
                        <td>{detailedForm.items.reduce((sum, item) => sum + (parseFloat(item.total_line) || 0), 0).toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </ResponsiveTable>
                  <button type="button" onClick={addDetailedItem}>+ Добавить позицию</button>
                  <button type="submit">Сохранить инвойс</button>
                </form>
              </div>
            )}

            <h3>Загрузить PDF (можно несколько)</h3>
            <input type="file" accept=".pdf,.xlsx,.xls" multiple onChange={handleBatchUpload} />
            {pdfLoading && <span style={{ marginLeft: '10px', color: 'blue' }}>Идёт обработка...</span>}

            {parsedInvoices.length > 0 && (
              <div style={{ border: '1px solid #1976d2', padding: '10px', marginTop: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <h4>Распознанные инвойсы ({parsedInvoices.length}) – Сумма: {totalQueueAmount} | Стеблей: {totalQueueStems}</h4>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                     <label style={{ whiteSpace: 'nowrap' }}>Дата поставки:</label>
                     <input type="date" value={queueDeliveryDate} onChange={e => setQueueDeliveryDate(e.target.value)} style={{ width: '140px' }} />
                     <label style={{ whiteSpace: 'nowrap' }}>Грузополучатель:</label>
                     <select value={queueConsignee} onChange={e => setQueueConsignee(e.target.value)} style={{ width: '150px' }}>
                     <option value="">Не выбран</option>
                     {consignees.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                     </select>
                    <button onClick={handleSaveAll} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '4px 8px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', width: 'auto' }}>Сохранить все</button>
                 </div>
                </div>
                {parsedInvoices.map((inv, idx) => {
                  const validation = getInvoiceValidation(inv);
                  const currency = inv.currency || suppliers.find(s => String(s.id) === String(inv.supplier_id))?.currency || '';
                  const queueKey = invoiceQueueKey(inv);
                  return (
                    <div key={`${queueKey}:${idx}`} style={{ 
                      border: validation.valid ? '2px solid #4caf50' : '2px solid #f44336', 
                      padding: '8px', 
                      margin: '5px 0', 
                      backgroundColor: validation.valid ? '#e8f5e9' : '#ffebee',
                      borderRadius: '4px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong>{inv.invoice_number ? `#${inv.invoice_number}` : (inv.awb_number ? `AWB ${inv.awb_number}` : '—')}</strong>
                        <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: validation.valid ? '#2e7d32' : '#c62828' }}>
                          {validation.valid ? '✅ Автоматические проверки пройдены' : '⚠️ Нужна проверка'}
                        </span>
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        от {emptyAsDash(inv.invoice_date)} на сумму {formatCurrencyAmount(inv.total_amount || 0, currency)} ({inv.items ? inv.items.length : 0} поз., {inv.items ? inv.items.reduce((s, i) => s + (i.total_stems||0), 0) : 0} стеб.)
                      </div>
                      {validation.valid && (
                        <div style={{ color: '#555', fontSize: '0.8em', marginTop: '4px' }}>
                          Проверены структура и арифметика; перед сохранением сверьте с оригиналом.
                        </div>
                      )}
                      {!validation.valid && (
                        <div style={{ color: '#c62828', fontSize: '0.85em', marginTop: '4px' }}>
                          ⚠️ {validation.issues.join('; ')}
                        </div>
                      )}
                      {inv.supplier_name && <span> | Поставщик: {inv.supplier_name}</span>}
                      {inv.awb_number && (
                        <span> | AWB: {inv.awb_number}
                          <button onClick={() => navigator.clipboard.writeText(inv.awb_number)} style={{ marginLeft: 5, padding: '0 5px', fontSize: '0.8em' }} title="Копировать">📋</button>
                        </span>
                      )}
                      {inv.transport_weight && <span> | Вес: {inv.transport_weight} кг</span>}
                      {inv.transport_cost && <span> | Транспорт: {formatCurrencyAmount(inv.transport_cost, currency)}</span>}
                      {inv.supplier_commission && <span> | Комиссия: {formatCurrencyAmount(inv.supplier_commission, currency)}</span>}
                      {inv.packaging_cost && <span> | Упаковка: {formatCurrencyAmount(inv.packaging_cost, currency)}</span>}
                      {inv.packaging_details && inv.packaging_details.length > 0 && <span> | Упаковка: {inv.packaging_details.length} видов</span>}
                      <div style={{ marginTop: '5px' }}>
                        <button onClick={() => fillDetailedForm(inv)} style={{ marginRight: 5, backgroundColor: '#2196f3', color: 'white', border: 'none', padding: '4px 8px', cursor: 'pointer' }}>Заполнить форму</button>
                        <button onClick={() => openEditModal(inv)} style={{ marginRight: 5, backgroundColor: '#ff9800', color: 'white', border: 'none', padding: '4px 8px', cursor: 'pointer' }}>Редактировать</button>
                        <button onClick={() => handleSaveOne(inv)} disabled={savingId === queueKey} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '4px 8px', cursor: 'pointer' }}>{savingId === queueKey ? 'Сохраняю...' : 'Сохранить'}</button>
                        <button onClick={() => handleRemoveParsed(inv)} style={{ marginLeft: 5, backgroundColor: '#f44336', color: 'white', border: 'none', padding: '4px 8px', cursor: 'pointer' }}>🗑️</button>
                        <button onClick={() => handleShowRaw(inv)} style={{ marginLeft: 5, backgroundColor: '#9e9e9e', color: 'white', border: 'none', padding: '4px 8px', cursor: 'pointer', fontSize: '0.85em' }}>RAW</button>
                        {!validation.valid && (
                          <button onClick={() => handleLearnParser(inv)} style={{ marginLeft: 5, backgroundColor: '#ff9800', color: 'white', border: 'none', padding: '4px 8px', cursor: 'pointer', fontSize: '0.85em' }}>🧠 Дообучить</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      case 'awb':
        return <div><h3>Внесение AWB</h3><p>Функционал будет добавлен позже.</p></div>;
      case 'check':
        return (
          <div>
            <h3>Проверка инвойса</h3>
            <div>
              <label>Поставщик:
                <select value={checkSupplier} onChange={e => setCheckSupplier(e.target.value)}>
                  <option value="">Выберите поставщика</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              {lastInvoices.length > 0 && (
                <ResponsiveTable border="1" style={{ marginTop: '10px' }}>
                  <thead><tr><th>Номер инвойса</th><th>Сумма</th></tr></thead>
                  <tbody>
                    {lastInvoices.map((inv, idx) => (
                      <tr key={`${inv.invoice_number || 'blank'}:${idx}`}><td>{inv.invoice_number}</td><td>{formatCurrencyAmount(inv.total_amount, inv.currency)}</td></tr>
                    ))}
                  </tbody>
                </ResponsiveTable>
              )}
            </div>
          </div>
        );
      case 'saved':
        return (
          <div>
            <h3>Сохранённые инвойсы</h3>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'end', marginBottom: '16px' }}>
              <label>Поставщик:
                <select
                  aria-label="Фильтр по поставщику"
                  value={savedFilters.supplierId}
                  onChange={e => setSavedFilters({...savedFilters, supplierId: e.target.value})}
                >
                  <option value="">Все поставщики</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label>Дата поставки:
                <input
                  aria-label="Фильтр по дате поставки"
                  type="date"
                  value={savedFilters.deliveryDate}
                  onChange={e => setSavedFilters({...savedFilters, deliveryDate: e.target.value})}
                />
              </label>
              <label style={{ minWidth: '260px' }}>Поиск по номеру или AWB:
                <input
                  aria-label="Поиск по номеру инвойса и AWB"
                  type="search"
                  value={savedFilters.query}
                  onChange={e => setSavedFilters({...savedFilters, query: e.target.value})}
                  placeholder="Номер инвойса или AWB"
                  style={{ width: '100%' }}
                />
              </label>
              <button
                type="button"
                onClick={() => setSavedFilters({ supplierId: '', deliveryDate: '', query: '' })}
              >
                Сбросить
              </button>
            </div>

            {savedInvoicesError && <p role="alert" style={{ color: '#c62828' }}>{savedInvoicesError}</p>}
            {savedInvoicesLoading ? (
              <p>Загрузка...</p>
            ) : (
              <>
                <p>
                  Найдено: <strong>{filteredSavedInvoices.length}</strong>
                  {' · '}Стеблей: <strong>{filteredSavedInvoices.reduce((sum, inv) => sum + invoiceStemCount(inv), 0)}</strong>
                  {' · '}Итог: <strong>{savedInvoiceTotals || '—'}</strong>
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <ResponsiveTable border="1" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1100px' }}>
                    <thead>
                      <tr>
                        <th>Поставщик</th>
                        <th>Номер / AWB</th>
                        <th>Дата инвойса</th>
                        <th>Дата поставки</th>
                        <th>Грузополучатель</th>
                        <th>Позиции</th>
                        <th>Стебли</th>
                        <th>Итог</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSavedInvoices.map(invoice => (
                        <tr key={invoice.id} data-testid={`saved-invoice-${invoice.id}`}>
                          <td>{emptyAsDash(invoice.supplier_name)}</td>
                          <td>{invoiceReference(invoice)}</td>
                          <td>{emptyAsDash(invoice.invoice_date)}</td>
                          <td>{emptyAsDash(invoice.delivery_date)}</td>
                          <td>{emptyAsDash(invoice.consignee)}</td>
                          <td style={{ textAlign: 'right' }}>{invoiceItemCount(invoice)}</td>
                          <td style={{ textAlign: 'right' }}>{invoiceStemCount(invoice)}</td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatCurrencyAmount(invoice.total_amount, invoice.currency) || '—'}</td>
                          <td><button type="button" onClick={() => setSelectedInvoice(invoice)}>Открыть</button></td>
                        </tr>
                      ))}
                      {filteredSavedInvoices.length === 0 && (
                        <tr><td colSpan="9" style={{ textAlign: 'center', padding: '16px' }}>Инвойсы не найдены</td></tr>
                      )}
                    </tbody>
                  </ResponsiveTable>
                </div>
              </>
            )}
          </div>
        );
      case 'export':
        return (
          <div>
            <h3>Выгрузка</h3>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <label>Дата поставки:
                <select value={exportStart} onChange={e => setExportStart(e.target.value)}>
                  <option value="">Выберите дату</option>
                  {deliveryDates.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </label>
              <label>Поставщик:
                <select value={exportEnd} onChange={e => setExportEnd(e.target.value)}>
                  <option value="">Все</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <button
                onClick={async () => {
                  try {
                    const params = {};
                    if (exportStart) params.delivery_date = exportStart;
                    if (exportEnd) params.supplier_id = exportEnd;
                    const response = await api.get('/tenant/invoices/export/excel', {
                      params,
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'invoices_export.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (err) {
                    alert('Ошибка при скачивании выгрузки');
                  }
                }}
                style={{ padding: '10px 20px', fontSize: '16px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                📥 Скачать Excel
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // --- Основной рендер ---
  return (
    <div className="purchaser-workflow">
      <div className="workspace-heading"><div><h1>Инвойсы и загрузка</h1><p>Загрузите документы, проверьте распознавание и выберите грузополучателя перед сохранением.</p></div></div>
      {!embeddedNavigation && <nav className="workspace-tabs" aria-label="Работа с инвойсами">
        {INVOICE_SECTIONS.map(([key,label]) =>
          <button key={key} type="button" aria-current={activeTab===key?'page':undefined} onClick={()=>setActiveTab(key)}>{label}</button>)}
      </nav>}
      <div className="purchaser-content">{renderTabContent()}</div>

      {/* Просмотр сохранённого инвойса */}
      {selectedInvoice && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Инвойс ${invoiceReference(selectedInvoice)}`}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000,
            overflowY: 'auto', padding: '35px 20px',
          }}
          onClick={() => setSelectedInvoice(null)}
        >
          <div
            style={{ backgroundColor: 'white', maxWidth: '1250px', margin: '0 auto', padding: '24px', borderRadius: '8px', position: 'relative' }}
            onClick={event => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Закрыть просмотр"
              onClick={() => setSelectedInvoice(null)}
              style={{ position: 'absolute', top: '12px', right: '12px', border: 0, background: 'transparent', fontSize: '22px', cursor: 'pointer' }}
            >✕</button>
            <h3 style={{ marginTop: 0 }}>Инвойс {invoiceReference(selectedInvoice)}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '10px 22px', marginBottom: '20px' }}>
              <div><strong>Поставщик:</strong> {emptyAsDash(selectedInvoice.supplier_name)}</div>
              <div><strong>Номер инвойса:</strong> {emptyAsDash(selectedInvoice.invoice_number)}</div>
              <div><strong>AWB:</strong> {emptyAsDash(selectedInvoice.awb_number)}</div>
              <div><strong>Дата инвойса:</strong> {emptyAsDash(selectedInvoice.invoice_date)}</div>
              <div><strong>Дата поставки:</strong> {emptyAsDash(selectedInvoice.delivery_date)}</div>
              <div><strong>Грузополучатель:</strong> {emptyAsDash(selectedInvoice.consignee)}</div>
              <div><strong>Вес:</strong> {selectedInvoice.transport_weight === null || selectedInvoice.transport_weight === undefined ? '—' : `${selectedInvoice.transport_weight} кг`}</div>
              <div><strong>Валюта:</strong> {emptyAsDash(selectedInvoice.currency)}</div>
            </div>

            <h4>Товары</h4>
            <div style={{ overflowX: 'auto' }}>
              <ResponsiveTable border="1" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1050px' }}>
                <thead>
                  <tr>
                    <th>№</th><th>Цветок</th><th>Сорт</th><th>Длина</th><th>Тип коробки</th><th>Номер коробки</th>
                    <th>Стеблей в банче</th><th>Банчей</th><th>Стебли</th><th>Цена</th><th>Сумма строки</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedInvoice.items || []).map((item, index) => (
                    <tr key={item.id || index} data-testid="saved-item-row">
                      <td>{index + 1}</td>
                      <td>{emptyAsDash(item.flower_type || item.product_name)}</td>
                      <td>{emptyAsDash(item.variety)}</td>
                      <td>{emptyAsDash(item.length)}</td>
                      <td>{emptyAsDash(item.box_type || item.box_dimension)}</td>
                      <td>{emptyAsDash(item.box_number)}</td>
                      <td>{emptyAsDash(item.stems_per_bunch)}</td>
                      <td>{emptyAsDash(item.bunches_per_box)}</td>
                      <td>{emptyAsDash(item.total_stems ?? item.quantity)}</td>
                      <td>{item.price_per_stem === null || item.price_per_stem === undefined ? '—' : formatCurrencyAmount(item.price_per_stem, selectedInvoice.currency)}</td>
                      <td>{item.total_line === null || item.total_line === undefined ? '—' : formatCurrencyAmount(item.total_line, selectedInvoice.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </ResponsiveTable>
            </div>

            <h4>Начисления и итоги</h4>
            <ResponsiveTable border="1" style={{ borderCollapse: 'collapse', minWidth: '420px' }}>
              <tbody>
                <tr><th>Товары</th><td>{formatCurrencyAmount(invoiceGoodsTotal(selectedInvoice), selectedInvoice.currency, true)}</td></tr>
                <tr><th>Упаковка</th><td>{selectedInvoice.packaging_cost === null || selectedInvoice.packaging_cost === undefined ? '—' : formatCurrencyAmount(selectedInvoice.packaging_cost, selectedInvoice.currency)}</td></tr>
                <tr><th>Комиссия</th><td>{selectedInvoice.supplier_commission === null || selectedInvoice.supplier_commission === undefined ? '—' : formatCurrencyAmount(selectedInvoice.supplier_commission, selectedInvoice.currency)}</td></tr>
                <tr><th>Транспорт</th><td>{selectedInvoice.transport_cost === null || selectedInvoice.transport_cost === undefined ? '—' : formatCurrencyAmount(selectedInvoice.transport_cost, selectedInvoice.currency)}</td></tr>
                <tr><th>Итого инвойса</th><td><strong>{formatCurrencyAmount(selectedInvoice.total_amount, selectedInvoice.currency) || '—'}</strong></td></tr>
              </tbody>
            </ResponsiveTable>

            {selectedInvoice.packaging_details?.length > 0 && (
              <div>
                <h4>Детали упаковки</h4>
                <ResponsiveTable border="1" style={{ borderCollapse: 'collapse' }}>
                  <thead><tr><th>Количество</th><th>Код</th><th>Описание</th><th>Сумма</th></tr></thead>
                  <tbody>
                    {selectedInvoice.packaging_details.map((pkg, index) => (
                      <tr key={index}>
                        <td>{emptyAsDash(pkg.qua)}</td><td>{emptyAsDash(pkg.code)}</td><td>{emptyAsDash(pkg.description)}</td>
                        <td>{pkg.amount === null || pkg.amount === undefined ? '—' : formatCurrencyAmount(pkg.amount, selectedInvoice.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </ResponsiveTable>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно редактирования */}
      {editModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            zIndex: 1000,
            overflowY: 'auto',
            paddingTop: '40px',
            paddingBottom: '40px'
          }}
          onClick={() => setEditModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '25px',
              borderRadius: '8px',
              maxWidth: '900px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              position: 'relative',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Редактирование инвойса {editForm.invoice_number}</h3>
            <div className="invoice-fields-grid" style={{ gap: '10px' }}>
              <label>Поставщик:
                <select value={editForm.supplier_id} onChange={e => setEditForm({...editForm, supplier_id: e.target.value})} required>
                  <option value="">Выберите</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label>Номер инвойса: <input type="text" value={editForm.invoice_number} onChange={e => setEditForm({...editForm, invoice_number: e.target.value})} /></label>
              <label>Дата инвойса: <input type="date" value={editForm.invoice_date} onChange={e => setEditForm({...editForm, invoice_date: e.target.value})} /></label>
              <label>Общая сумма: <input type="number" value={editForm.total_amount} onChange={e => setEditForm({...editForm, total_amount: e.target.value})} /></label>
              <label>AWB: <input type="text" value={editForm.awb_number} onChange={e => setEditForm({...editForm, awb_number: e.target.value})} /></label>
              <label>Вес поставки (кг): <input type="number" value={editForm.transport_weight} onChange={e => setEditForm({...editForm, transport_weight: e.target.value})} /></label>
              <label>Стоимость транспорта: <input type="number" value={editForm.transport_cost} onChange={e => setEditForm({...editForm, transport_cost: e.target.value})} /></label>
              <label>Комиссия поставщика: <input type="number" value={editForm.supplier_commission} onChange={e => setEditForm({...editForm, supplier_commission: e.target.value})} /></label>
              <label>Стоимость упаковки: <input type="number" value={editForm.packaging_cost} onChange={e => setEditForm({...editForm, packaging_cost: e.target.value})} /></label>
            </div>

            {editForm.packaging_details && editForm.packaging_details.length > 0 && (
              <div style={{ marginTop: '10px', border: '1px solid #ccc', padding: '8px' }}>
                <h4>Упаковка поставки</h4>
                <ResponsiveTable border="1" style={{ width: '100%' }}>
                  <thead><tr><th>Количество коробок</th><th>Код коробки</th><th>Тип коробки</th><th>Стоимость за коробки</th></tr></thead>
                  <tbody>
                    {editForm.packaging_details.map((pkg, idx) => (
                      <tr key={idx}><td>{pkg.qua}</td><td>{pkg.code}</td><td>{pkg.description}</td><td>{pkg.amount}</td></tr>
                    ))}
                  </tbody>
                </ResponsiveTable>
              </div>
            )}

            <h4>Позиции</h4>
            <ResponsiveTable border="1" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Вид цветка</th><th>Сорт</th><th>Высота</th><th>Разбанчевка</th><th>Тип коробки</th><th>Номер коробки</th><th>Банчей в коробке</th><th>Итого стеблей</th><th>Цена за штуку</th><th>Цена общая</th>
                </tr>
              </thead>
              <tbody>
                {editForm.items.map((item, idx) => (
                  <tr key={idx}>
                    <td><input type="text" value={item.flower_type} onChange={e => { const newItems = [...editForm.items]; newItems[idx].flower_type = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                    <td><input type="text" value={item.variety} onChange={e => { const newItems = [...editForm.items]; newItems[idx].variety = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                    <td><input type="text" value={item.length} onChange={e => { const newItems = [...editForm.items]; newItems[idx].length = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                    <td><input type="number" value={item.stems_per_bunch} onChange={e => { const newItems = [...editForm.items]; newItems[idx].stems_per_bunch = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                    <td><input type="text" value={item.box_dimension} onChange={e => { const newItems = [...editForm.items]; newItems[idx].box_dimension = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                    <td><input type="text" value={item.box_number} onChange={e => { const newItems = [...editForm.items]; newItems[idx].box_number = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                    <td><input type="number" value={item.bunches_per_box} onChange={e => { const newItems = [...editForm.items]; newItems[idx].bunches_per_box = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                    <td><input type="number" value={item.total_stems} onChange={e => { const newItems = [...editForm.items]; newItems[idx].total_stems = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                    <td><input type="number" value={item.price_per_stem} onChange={e => { const newItems = [...editForm.items]; newItems[idx].price_per_stem = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                    <td><input type="number" value={item.total_line} readOnly /></td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
            <div style={{ marginTop: '15px' }}>
              <button onClick={handleEditSave} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '10px 20px', marginRight: '10px', borderRadius: '4px', cursor: 'pointer' }}>Сохранить</button>
              <button onClick={() => setEditModalOpen(false)} style={{ backgroundColor: '#ccc', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer' }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно RAW */}
      {rawModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            zIndex: 2000,
            overflowY: 'auto',
            paddingTop: '40px',
            paddingBottom: '40px'
          }}
          onClick={() => setRawModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '25px',
              borderRadius: '8px',
              maxWidth: '900px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              position: 'relative',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Исходный текст инвойса</h3>
            <button onClick={() => setRawModalOpen(false)} style={{ position: 'absolute', top: '10px', right: '10px', border: 'none', background: 'transparent', fontSize: '20px', cursor: 'pointer' }}>✕</button>
            <pre style={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-word', 
              backgroundColor: '#f5f5f5', 
              padding: '15px', 
              borderRadius: '4px',
              fontSize: '12px',
              lineHeight: 1.5,
              maxHeight: '70vh',
              overflowY: 'auto'
            }}>
              {rawText}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

export default PurchaserDashboard;
